// Brands vs athletes is a deploy-level distinction (NEXT_PUBLIC_BRANDS_APP env +
// next.config.js rewrites). Both deploys share one Clerk app — auth methods are toggled
// in Clerk Dashboard and visible to all users; afterSignInUrl="/dashboard" rewrites to
// the correct flavour per deploy. There is no in-app brand/athlete discrimination today.
import React from 'react';
import { SignIn, useAuth } from '@clerk/nextjs';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import { getCircleSessionUrl } from 'lib/circle';
import { getSatelliteSignInRedirect } from 'lib/clerk/satellite';
import request, { matchError, unwrapResult } from 'lib/request';

const SATELLITE_HOST = process.env.NEXT_PUBLIC_CLERK_SATELLITE_HOST;
const PRIMARY_SIGN_IN_URL = process.env.NEXT_PUBLIC_CLERK_PRIMARY_SIGN_IN_URL;

const wrap = css`
  display: flex;
  justify-content: center;
  padding: 40px 16px;
  width: 100%;
`;

export default function AthleteLoginPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const router = useRouter();
  const redirected = React.useRef(false);

  // `c` marks a Circle SSO entry: the backend /o/authorize/ bounces an unauthenticated
  // visitor here as /signin?c=1. Wait for router.isReady before deciding — query params are
  // empty on the first client render, and a premature /dashboard redirect would drop `c`.
  const isCircle = !!router.query.c;

  React.useEffect(() => {
    if (!isLoaded || !router.isReady || redirected.current) {
      return;
    }

    // A browser that already has a live Clerk session must never mount <SignIn>. Clerk's
    // component re-detects the session on every render and re-issues its afterSignInUrl
    // navigation; with no guard on this page that bounce races the destination's SSR
    // redirects and produces the /signin <-> /dashboard loop. Take the redirect over
    // ourselves — exactly once (the ref guards against re-fires) and via a hard nav, the
    // same race-avoidance trick SignOut uses, so it doesn't fight Clerk's own client nav.
    if (isSignedIn) {
      redirected.current = true;

      // Circle SSO: /o/authorize/ relies on a backend Django session that Clerk never creates
      // (and can't reach cross-apex on .com), so it bounced here. Complete the login via
      // Circle's token-cookie endpoint instead: mint a Circle token (Clerk Bearer) and hand
      // off. Fall back to /dashboard if the mint fails so the user isn't stranded.
      if (isCircle) {
        request<{ token: string }>('circle-token', { method: 'POST' }).then((result) => {
          if (matchError(result)) {
            window.location.replace('/dashboard');
            return;
          }
          window.location.replace(getCircleSessionUrl(unwrapResult(result).payload.token));
        });
        return;
      }

      window.location.replace('/dashboard');
      return;
    }

    // Satellite domain (.co): credential entry is not allowed here — Clerk rejects it with
    // "This operation is not allowed on a satellite domain". Bounce to the PRIMARY (.com)
    // sign-in, carrying this URL as redirect_url so Clerk returns and syncs the session back.
    const satelliteRedirect = getSatelliteSignInRedirect(
      { host: window.location.host, href: window.location.href },
      SATELLITE_HOST,
      PRIMARY_SIGN_IN_URL
    );
    if (satelliteRedirect) {
      redirected.current = true;
      window.location.replace(satelliteRedirect);
    }
  }, [isLoaded, isSignedIn, isCircle, router.isReady]);

  // isLoaded is only ever true client-side (Clerk hydrates in the browser), so window is
  // defined whenever this evaluates true — no SSR/hydration access to window.
  const onSatellite =
    isLoaded && !!SATELLITE_HOST && typeof window !== 'undefined' && window.location.host === SATELLITE_HOST;

  // Render nothing while Clerk is still hydrating, before the router has parsed the query (so
  // afterSignInUrl below sees `c`), when the visitor is signed in (about to be redirected), or
  // on the satellite domain — there mounting <SignIn> would render the form and Clerk rejects
  // the sign-in op; the effect above redirects to the primary instead.
  if (!isLoaded || !router.isReady || isSignedIn || onSatellite) {
    return <div css={wrap} />;
  }

  return (
    <div css={wrap}>
      {/* Athlete self-service sign-up is disabled. Hide the "No account? Sign up" footer
          action and point signUpUrl at /signin so no internal fallback reaches a registration
          screen. Real enforcement lives in the Clerk Dashboard "Restrictions" setting. */}
      <SignIn
        routing="hash"
        signUpUrl="/signin"
        // For a fresh Circle sign-in, return to /signin?c=1 (not /dashboard) so the effect above
        // can complete the Circle token-cookie handoff once the session exists.
        afterSignInUrl={isCircle ? '/signin?c=1' : '/dashboard'}
        appearance={{ elements: { footerAction: { display: 'none' } } }}
      />
    </div>
  );
}
